/**
 * Wing formations (M6 spec §6.2; plan P25): wedge and line slots around the player, in float64 world positions.
 *
 * Slot `i` (0-based) sits on rank `floor(i / 2) + 1`, left for an even `i` and right for an odd one, `rank × spacingM`
 * across. A wedge also steps `rank × backM` behind the nose (local +Z; the nose is −Z); a line keeps abreast. The
 * offset is rotated by the player's rotation and added to the player's float64 position (D-013: true positions stay
 * float64; the renderer and physics rebase them, nothing here touches three.js or Rapier).
 *
 * Allocation-free: module scratch and caller-owned typed arrays (D-014).
 */
import { rotateVec, type Quat, type Vec3 } from '../core/math'
import { Pos64, Rot } from '../world/components'

/** Formation codes (a const object: `erasableSyntaxOnly`). */
export const FORMATION = { wedge: 0, line: 1 } as const
export type FormationName = keyof typeof FORMATION
export type Formation = (typeof FORMATION)[FormationName]
export const FORMATION_NAMES = ['wedge', 'line'] as const satisfies readonly FormationName[]

export interface FormationSpacing {
  /** Across per rank, metres. */
  readonly spacingM: number
  /** Behind per rank in a wedge, metres. */
  readonly backM: number
}

// Module scratch (D-014).
const q: Quat = { x: 0, y: 0, z: 0, w: 1 }
const off: Vec3 = { x: 0, y: 0, z: 0 }

/** Writes slot `i`'s offset in the leader's local frame (metres) into `out`. */
export function slotLocalOffset(formation: Formation, i: number, spacing: FormationSpacing, out: Vec3): Vec3 {
  const rank = (i >> 1) + 1
  const side = (i & 1) === 0 ? -1 : 1
  out.x = side * rank * spacing.spacingM
  out.y = 0
  out.z = formation === FORMATION.wedge ? rank * spacing.backM : 0
  return out
}

/**
 * Writes slot `i`'s float64 world position around leader `leader` into `out[3i..3i+2]`: the local offset rotated by
 * the leader's rotation plus its `Pos64`.
 */
export function writeSlotWorld(formation: Formation, i: number, spacing: FormationSpacing, leader: number, out: Float64Array): void {
  slotLocalOffset(formation, i, spacing, off)
  q.x = Rot.x[leader]
  q.y = Rot.y[leader]
  q.z = Rot.z[leader]
  q.w = Rot.w[leader]
  rotateVec(q, off.x, off.y, off.z, off)
  const k = i * 3
  out[k] = Pos64.x[leader] + off.x
  out[k + 1] = Pos64.y[leader] + off.y
  out[k + 2] = Pos64.z[leader] + off.z
}

/** Writes slots 0..count−1 (see `writeSlotWorld`). */
export function writeFormation(formation: Formation, count: number, spacing: FormationSpacing, leader: number, out: Float64Array): void {
  for (let i = 0; i < count; i++) writeSlotWorld(formation, i, spacing, leader, out)
}
